function _createForOfIteratorHelper(r, e) { var t = "undefined" != typeof Symbol && r[Symbol.iterator] || r["@@iterator"]; if (!t) { if (Array.isArray(r) || (t = _unsupportedIterableToArray(r)) || e && r && "number" == typeof r.length) { t && (r = t); var _n = 0, F = function F() {}; return { s: F, n: function n() { return _n >= r.length ? { done: !0 } : { done: !1, value: r[_n++] }; }, e: function e(r) { throw r; }, f: F }; } throw new TypeError("Invalid attempt to iterate non-iterable instance.\nIn order to be iterable, non-array objects must have a [Symbol.iterator]() method."); } var o, a = !0, u = !1; return { s: function s() { t = t.call(r); }, n: function n() { var r = t.next(); return a = r.done, r; }, e: function e(r) { u = !0, o = r; }, f: function f() { try { a || null == t["return"] || t["return"](); } finally { if (u) throw o; } } }; }
function _unsupportedIterableToArray(r, a) { if (r) { if ("string" == typeof r) return _arrayLikeToArray(r, a); var t = {}.toString.call(r).slice(8, -1); return "Object" === t && r.constructor && (t = r.constructor.name), "Map" === t || "Set" === t ? Array.from(r) : "Arguments" === t || /^(?:Ui|I)nt(?:8|16|32)(?:Clamped)?Array$/.test(t) ? _arrayLikeToArray(r, a) : void 0; } }
function _arrayLikeToArray(r, a) { (null == a || a > r.length) && (a = r.length); for (var e = 0, n = Array(a); e < a; e++) n[e] = r[e]; return n; }
import { isNodeShadowRoot } from '../browser/htmlDomUtils';
import { DEFAULT_PROGRAMMATIC_ACTION_NAME_ATTRIBUTE } from './action/actionNameConstants';
/**
 * Marker used to indicate shadow DOM boundaries in selectors.
 * Named after the deprecated Shadow DOM v0 '::shadow' pseudo-element which served the same
 * purpose of marking entry into a shadow root.
 * Note: This is NOT a valid CSS selector, it's an internal marker that requires custom
 * parsing logic.
 */
export var SHADOW_DOM_MARKER = '::shadow ';
/**
 * Stable attributes are attributes that are commonly used to identify parts of a UI (ex:
 * component). Those attribute values should not be generated randomly (hardcoded most of the time)
 * and stay the same across deploys. They are not necessarily unique across the document.
 */
export var STABLE_ATTRIBUTES = [DEFAULT_PROGRAMMATIC_ACTION_NAME_ATTRIBUTE,
// Common test attributes (list provided by google recorder)
'data-testid', 'data-test', 'data-qa', 'data-cy', 'data-test-id', 'data-qa-id', 'data-testing',
// FullStory decorator attributes:
'data-component', 'data-element', 'data-source-file'];
// Selectors to use if they target a single element on the whole document. Those selectors are
// considered as "stable" and uniquely identify an element regardless of the page state. If we find
// one, we should consider the selector "complete" and stop iterating over ancestors.
var GLOBALLY_UNIQUE_SELECTOR_GETTERS = [getStableAttributeSelector, getIDSelector];
// Selectors to use if they target a single element among an element descendants. Those selectors
// are more brittle than "globally unique" selectors and should be combined with ancestor selectors
// to improve specificity.
var UNIQUE_AMONG_CHILDREN_SELECTOR_GETTERS = [getStableAttributeSelector, getClassSelector, getTagNameSelector];
export function getSelectorFromElement(targetElement, actionNameAttribute) {
  if (!targetElement.isConnected) {
    // We cannot compute a selector for a detached element, as we don't have access to all of its
    // parents, and we cannot determine if it's unique in the document.
    return;
  }
  var subtrees = getAllSubtreeTargets(targetElement);
  var selectorParts = [];
  var _iterator = _createForOfIteratorHelper(subtrees),
    _step;
  try {
    for (_iterator.s(); !(_step = _iterator.n()).done;) {
      var _step$value = _step.value,
        rootNode = _step$value.rootNode,
        target = _step$value.target;
      var selector = getSelectorFromElementWithinSubtree(target, rootNode, actionNameAttribute);
      if (!selector) {
        return undefined;
      }
      selectorParts.push(selector);
    }
  } catch (err) {
    _iterator.e(err);
  } finally {
    _iterator.f();
  }
  return selectorParts.join(SHADOW_DOM_MARKER);
}
/**
 * Returns all (rootNode, target) pairs from the document down to the element.
 */
function getAllSubtreeTargets(element) {
  var result = [];
  var currentTarget = element;
  while (currentTarget) {
    var rootNode = currentTarget.getRootNode();
    result.push({
      rootNode: rootNode,
      target: currentTarget
    });
    if (isNodeShadowRoot(rootNode)) {
      currentTarget = rootNode.host;
    } else {
      break;
    }
  }
  return result.reverse();
}
/**
 * Computes a CSS selector for an element within a specific subtree (document or shadow root).
 */
function getSelectorFromElementWithinSubtree(targetElement, rootNode, actionNameAttribute) {
  var currentSelector;
  var currentElement = targetElement;
  while (currentElement && currentElement.nodeName !== 'HTML') {
    var globallyUniqueSelector = findSelector(currentElement, rootNode, GLOBALLY_UNIQUE_SELECTOR_GETTERS, isSelectorUniqueWithinRoot, actionNameAttribute, currentSelector);
    if (globallyUniqueSelector) {
      return combineSelector(globallyUniqueSelector, currentSelector);
    }
    var uniqueSelectorAmongChildren = findSelector(currentElement, rootNode, UNIQUE_AMONG_CHILDREN_SELECTOR_GETTERS, isSelectorUniqueAmongSiblings, actionNameAttribute, currentSelector);
    var elementSelector = uniqueSelectorAmongChildren || getPositionSelector(currentElement);
    currentSelector = combineSelector(elementSelector, currentSelector);
    currentElement = currentElement.parentElement;
  }
  return currentSelector;
}
function isGeneratedValue(value) {
  // To compute the "URL path group", the backend replaces every URL path parts as a question mark
  // if it thinks the part is an identifier. The condition it uses is to checks whether a digit is
  // present.
  //
  // Here, we use the same strategy: if the value contains a digit, we consider it generated. This
  // strategy might be a bit naive and fail in some cases, but there are many fallbacks to generate
  // CSS selectors so it should be fine most of the time.
  return /[0-9]/.test(value);
}
function getIDSelector(element) {
  if (element.id && !isGeneratedValue(element.id)) {
    return "#".concat(CSS.escape(element.id));
  }
}
function getClassSelector(element) {
  if (element.tagName === 'BODY') {
    return;
  }
  var classList = element.classList;
  for (var i = 0; i < classList.length; i += 1) {
    var className = classList[i];
    if (isGeneratedValue(className)) {
      continue;
    }
    return "".concat(CSS.escape(element.tagName), ".").concat(CSS.escape(className));
  }
}
function getTagNameSelector(element) {
  return CSS.escape(element.tagName);
}
function getStableAttributeSelector(element, actionNameAttribute) {
  if (actionNameAttribute) {
    var selector = getAttributeSelector(actionNameAttribute);
    if (selector) {
      return selector;
    }
  }
  var _iterator2 = _createForOfIteratorHelper(STABLE_ATTRIBUTES),
    _step2;
  try {
    for (_iterator2.s(); !(_step2 = _iterator2.n()).done;) {
      var attributeName = _step2.value;
      var _selector = getAttributeSelector(attributeName);
      if (_selector) {
        return _selector;
      }
    }
  } catch (err) {
    _iterator2.e(err);
  } finally {
    _iterator2.f();
  }
  function getAttributeSelector(attributeName) {
    if (element.hasAttribute(attributeName)) {
      return "".concat(CSS.escape(element.tagName), "[").concat(attributeName, "=\"").concat(CSS.escape(element.getAttribute(attributeName)), "\"]");
    }
  }
}
function getPositionSelector(element) {
  var parent = element.parentNode;
  var sibling = parent.firstElementChild;
  var elementIndex = 1;
  while (sibling && sibling !== element) {
    if (sibling.tagName === element.tagName) {
      elementIndex += 1;
    }
    sibling = sibling.nextElementSibling;
  }
  return "".concat(CSS.escape(element.tagName), ":nth-of-type(").concat(elementIndex, ")");
}
function findSelector(element, rootNode, selectorGetters, predicate, actionNameAttribute, childSelector) {
  var _iterator3 = _createForOfIteratorHelper(selectorGetters),
    _step3;
  try {
    for (_iterator3.s(); !(_step3 = _iterator3.n()).done;) {
      var selectorGetter = _step3.value;
      var elementSelector = selectorGetter(element, actionNameAttribute);
      if (!elementSelector) {
        continue;
      }
      if (predicate(element, rootNode, elementSelector, childSelector)) {
        return elementSelector;
      }
    }
  } catch (err) {
    _iterator3.e(err);
  } finally {
    _iterator3.f();
  }
}
/**
 * Check whether the selector is unique within the root node (document or shadow root).
 */
function isSelectorUniqueWithinRoot(_element, rootNode, elementSelector, childSelector) {
  return rootNode.querySelectorAll(combineSelector(elementSelector, childSelector)).length === 1;
}
/**
 * Check whether the selector is unique among the element siblings. In other words, it returns true
 * if "ELEMENT_PARENT > CHILD_SELECTOR" returns a single element.
 *
 * @param currentElement - the element being considered while iterating over the target
 * element ancestors.
 * @param _rootNode - the root node (document or shadow root) - unused but required for predicate signature.
 * @param currentElementSelector - a selector that matches the current element. That
 * selector is not a composed selector (i.e. it might be a single tag name, class name...).
 * @param childSelector - child selector is a selector that targets a descendant
 * of the current element. When undefined, the current element is the target element.
 *
 * # Scope selector usage
 *
 * When composed together, the final selector will be joined with `>` operators to make sure we
 * target direct descendants at each level. In this function, we'll use `querySelector` to check if
 * a selector matches descendants of the current element. But by default, the query selector match
 * elements at any level. Example:
 *
 * ```html
 * <main>
 *   <div>
 *     <span></span>
 *   </div>
 *   <marquee>
 *     <div>
 *       <span></span>
 *     </div>
 *   </marquee>
 * </main>
 * ```
 *
 * `sibling.querySelector('DIV > SPAN')` will match both span elements, so we would consider the
 * selector to be not unique, even if it is unique when we'll compose it with the parent with a `>`
 * operator (`MAIN > DIV > SPAN`).
 *
 * To avoid this, we can use the `:scope` selector to make sure the selector starts from the current
 * sibling (i.e. `sibling.querySelector('DIV:scope > SPAN')` will only match the first span).
 *
 * [1]: https://developer.mozilla.org/fr/docs/Web/CSS/:scope
 *
 * # Performance considerations
 *
 * We compute selectors in performance-critical operations (ex: during a click), so we need to make
 * sure the function is as fast as possible. We observed that naively using `querySelectorAll` to
 * check if the selector matches more than 1 element is quite expensive, so we want to avoid it.
 *
 * Because we are iterating the DOM upward and we use that function at every level, we know the
 * child selector is already unique among the current element children, so we don't need to check
 * for the current element subtree.
 *
 * Instead, we can focus on the current element siblings. If we find a single element matching the
 * selector within a sibling, we know that it's not unique. This allows us to use `querySelector`
 * (or `matches`, when the current element is the target element) instead of `querySelectorAll`.
 */
export function isSelectorUniqueAmongSiblings(currentElement, _rootNode, currentElementSelector, childSelector) {
  var isSiblingMatching;
  if (childSelector === undefined) {
    // If the child selector is undefined (meaning `currentElement` is the target element, not one
    // of its ancestor), we need to use `matches` to check if the sibling is matching the selector,
    // as `querySelector` only returns a descendant of the element.
    isSiblingMatching = function isSiblingMatching(sibling) {
      return sibling.matches(currentElementSelector);
    };
  } else {
    var scopedSelector = combineSelector("".concat(currentElementSelector, ":scope"), childSelector);
    isSiblingMatching = function isSiblingMatching(sibling) {
      return sibling.querySelector(scopedSelector) !== null;
    };
  }
  // Check siblings by iterating directly through previousElementSibling and nextElementSibling.
  // This works even when parentElement is null (e.g., when parent is a DocumentFragment).
  // Check previous siblings
  var sibling = currentElement.previousElementSibling;
  while (sibling) {
    if (isSiblingMatching(sibling)) {
      return false;
    }
    sibling = sibling.previousElementSibling;
  }
  // Check next siblings
  sibling = currentElement.nextElementSibling;
  while (sibling) {
    if (isSiblingMatching(sibling)) {
      return false;
    }
    sibling = sibling.nextElementSibling;
  }
  return true;
}
function combineSelector(parent, child) {
  return child ? "".concat(parent, ">").concat(child) : parent;
}